/* eslint-disable bsky-internal/avoid-unwrapped-text */
import {useController} from 'react-hook-form'

import {ROUTES} from '#/lib/routes/constants.ts'
import {type SignUpFormData} from '#/lib/schemas/sign-up'
import {useIsMobileWeb} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import LockIcon from '#/components/icons/LockIcon'
import MailIcon from '#/components/icons/MailIcon'
import MailIcon2 from '#/components/icons/MailIcon2'
import PersonIcon from '#/components/icons/PersonIcon'
import {AccentLink} from '#/wsocial/components/primitives'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {
  Checkbox,
  ErrorMsg,
  LogoBlock,
  MobileTopNav,
  PageShell,
  SupportFooter,
} from './primitives'
import {inputRowStyle, inputStyle, labelStyle} from './styles'
import {type SignUpForm} from './types'

export function StepAccount1({
  form,
  onBack,
  onNext,
  error,
  hasPrefilledEmail,
}: {
  form: SignUpForm
  onBack?: () => void
  onNext: () => void
  error: string
  hasPrefilledEmail?: boolean
}) {
  const tokens = useSemanticTokens()
  const isMobileWeb = useIsMobileWeb()
  const {field: nameField} = useController<SignUpFormData, 'name'>({
    control: form.control,
    name: 'name',
  })
  const {field: emailField, fieldState: emailState} = useController<
    SignUpFormData,
    'email'
  >({
    control: form.control,
    name: 'email',
  })
  const {field: passwordField, fieldState: passwordState} = useController<
    SignUpFormData,
    'password'
  >({
    control: form.control,
    name: 'password',
  })
  const {field: isAdultField, fieldState: isAdultState} = useController<
    SignUpFormData,
    'isAdult'
  >({
    control: form.control,
    name: 'isAdult',
  })
  const {field: agreedField, fieldState: agreedState} = useController<
    SignUpFormData,
    'agreedToTerms'
  >({
    control: form.control,
    name: 'agreedToTerms',
  })

  const canContinue =
    nameField.value.trim() &&
    emailField.value.trim() &&
    passwordField.value.length >= 8 &&
    isAdultField.value &&
    agreedField.value

  const handleNext = async () => {
    const isValid = await form.trigger([
      'name',
      'email',
      'password',
      'isAdult',
      'agreedToTerms',
    ])
    if (isValid) onNext()
  }

  const checkboxError =
    isAdultState.error?.message && agreedState.error?.message
      ? `${isAdultState.error?.message} / ${agreedState.error?.message}`
      : isAdultState.error?.message || agreedState.error?.message

  return (
    <PageShell>
      {isMobileWeb && onBack && <MobileTopNav onBack={onBack} />}
      <LogoBlock />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '0 16px',
        }}>
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: '17px',
            color: tokens.onboarding.textMedium,
            margin: 0,
          }}>
          Step 1 of 2
        </p>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 700,
            lineHeight: '35px',
            color: '#fff',
            margin: 0,
          }}>
          Create your account
        </h1>
        <p
          style={{
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '17px',
            color: tokens.onboarding.textMedium,
            margin: '4px 0 0',
          }}>
          Welcome to W. We value real people and genuine conversations. To write
          posts or take part in conversations, you need to verify that you are a
          real person. We encourage real names, but you may also choose to write
          anonymously.
        </p>
      </div>

      <div
        style={{
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}>
        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          <label style={labelStyle(tokens)}>Name</label>
          <div style={inputRowStyle(tokens)}>
            <PersonIcon />
            <input
              type="text"
              placeholder="Your full real name"
              value={nameField.value}
              onChange={e => nameField.onChange(e.target.value)}
              onBlur={nameField.onBlur}
              className="SignInInput"
              style={inputStyle}
              autoComplete="name"
              data-testid="nameInput"
            />
          </div>
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          <label style={labelStyle(tokens)}>Email</label>
          <div style={inputRowStyle(tokens)}>
            <MailIcon />
            <input
              type="email"
              placeholder="inviteduser@mail.com"
              value={emailField.value}
              onChange={e => emailField.onChange(e.target.value)}
              onBlur={emailField.onBlur}
              className="SignInInput"
              style={inputStyle}
              autoComplete="email"
              disabled={hasPrefilledEmail}
              data-testid="emailInput"
            />
          </div>
          <ErrorMsg msg={emailState.error?.message || error} />
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          <label style={labelStyle(tokens)}>Password</label>
          <div style={inputRowStyle(tokens)}>
            <LockIcon />
            <input
              type="password"
              placeholder="Choose your password"
              value={passwordField.value}
              onChange={e => passwordField.onChange(e.target.value)}
              onBlur={passwordField.onBlur}
              className="SignInInput"
              style={inputStyle}
              autoComplete="new-password"
              data-testid="passwordInput"
            />
          </div>
          <ErrorMsg msg={passwordState.error?.message || ''} />
        </div>

        <Checkbox
          checked={isAdultField.value}
          onChange={v => isAdultField.onChange(v)}
          label="I am 18 or older"
        />
        <Checkbox
          checked={agreedField.value}
          onChange={v => agreedField.onChange(v)}
          label={
            <>
              I agree to the{' '}
              <AccentLink href={ROUTES.TOS}>Terms of use</AccentLink> and the{' '}
              <AccentLink href={ROUTES.PRIVACY_POLICY}>
                Privacy policy
              </AccentLink>
            </>
          }
        />
        <ErrorMsg msg={checkboxError || ''} />
      </div>

      <div
        style={{
          padding: '16px 16px',
          display: 'flex',
          justifyContent: isMobileWeb ? undefined : 'flex-end',
        }}>
        <Button
          label="Next"
          color="primary"
          size="large"
          testID="account1NextBtn"
          onPress={() => void handleNext()}
          disabled={!canContinue}
          style={isMobileWeb ? {alignSelf: 'stretch'} : undefined}>
          <ButtonText>Next</ButtonText>
        </Button>
      </div>

      <SupportFooter />
    </PageShell>
  )
}

export function StepAccount2({
  form,
  onBack,
  onSubmit,
  error,
  isProcessing,
  pdsDomain,
}: {
  form: SignUpForm
  onBack: () => void
  onSubmit: () => void
  error: string
  isProcessing: boolean
  pdsDomain: string
}) {
  const tokens = useSemanticTokens()
  const isMobileWeb = useIsMobileWeb()
  const {field: handleField, fieldState: handleState} = useController<
    SignUpFormData,
    'handle'
  >({
    control: form.control,
    name: 'handle',
  })

  const handleSubmit = async () => {
    const isValid = await form.trigger(['handle'])
    if (isValid) onSubmit()
  }

  return (
    <PageShell>
      {isMobileWeb && <MobileTopNav onBack={onBack} />}
      <LogoBlock />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '0 16px',
        }}>
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: '17px',
            color: tokens.onboarding.textMedium,
            margin: 0,
          }}>
          Step 2 of 2
        </p>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 700,
            lineHeight: '35px',
            color: '#fff',
            margin: 0,
          }}>
          Choose your username
        </h1>
        <p
          style={{
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '17px',
            color: tokens.onboarding.textMedium,
            margin: 0,
          }}>
          You can change your username later.
        </p>
      </div>

      <div
        style={{
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}>
        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          <label style={labelStyle(tokens)}>Username</label>
          <div style={inputRowStyle(tokens)}>
            <MailIcon2 />
            <input
              type="text"
              placeholder="username.wsocial.eu"
              value={handleField.value}
              onChange={e =>
                handleField.onChange(
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                )
              }
              onBlur={handleField.onBlur}
              onKeyDown={e => {
                if (e.key === 'Enter' && handleField.value) void handleSubmit()
              }}
              className="SignInInput"
              style={inputStyle}
              autoComplete="username"
              autoCapitalize="none"
              data-testid="handleInput"
            />
          </div>
          {handleField.value && !handleField.value.includes('.') && (
            <p
              style={{
                fontSize: 12,
                color: tokens.onboarding.textMedium,
                margin: 0,
                paddingLeft: 4,
              }}>
              Handle:{' '}
              <span style={{color: tokens.onboarding.textMedium}}>
                @{handleField.value}.{pdsDomain}
              </span>{' '}
              — Only lowercase English letters, numbers and hyphens (-) in the
              middle are allowed.
            </p>
          )}
        </div>

        <ErrorMsg msg={handleState.error?.message || error} />
      </div>

      {isMobileWeb ? (
        <div
          style={{
            padding: '16px 16px',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Button
            label="Create account"
            color="primary"
            size="large"
            testID="createAccountBtn"
            onPress={() => void handleSubmit()}
            disabled={!handleField.value.trim() || isProcessing}
            style={{alignSelf: 'stretch'}}>
            <ButtonText>{isProcessing ? '…' : 'Create account'}</ButtonText>
          </Button>
        </div>
      ) : (
        <div
          style={{
            padding: '16px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Button
            label="Back"
            color="secondary"
            size="large"
            testID="account2BackBtn"
            onPress={onBack}
            disabled={isProcessing}>
            <ButtonText>Back</ButtonText>
          </Button>
          <Button
            label="Create account"
            color="primary"
            size="large"
            testID="createAccountBtn"
            onPress={() => void handleSubmit()}
            disabled={!handleField.value.trim() || isProcessing}>
            <ButtonText>{isProcessing ? '…' : 'Create account'}</ButtonText>
          </Button>
        </div>
      )}

      <SupportFooter />
    </PageShell>
  )
}
